import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentOrganization } from "../../../app/slices/UserSlice";
import {
  initializeAnimationLibrary,
  resetAnimationLibrary,
  selectAnimationLibraryFilter,
  selectPagedAnimations,
  setAnimationLibraryFilter,
  unloadedAnimation,
  updateAnimationLibrary,
} from "../../../app/slices/AnimationsSlice";
import { AnimationService } from "../../../services/AnimationService";

export default function useAnimationLibraryPagination() {
  const dispatch = useDispatch();
  const currentOrganization = useSelector(selectCurrentOrganization);
  const pagedAnimations = useSelector(selectPagedAnimations);
  const filter = useSelector(selectAnimationLibraryFilter);
  const [pageSize, setPageSize] = useState(10);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const setFilter = useCallback(
    (nextFilter: string) => dispatch(setAnimationLibraryFilter(nextFilter)),
    [dispatch]
  );

  useEffect(() => {
    loadFirstPage();
  }, [currentOrganization?.id, filter]);

  const loadFirstPage = async () => {
    if (!currentOrganization?.id) return;

    const result = await AnimationService.GetAnimations(
      currentOrganization.id,
      1,
      pageSize,
      { name: filter }
    );
    if (!result) return;

    dispatch(
      initializeAnimationLibrary({
        totalCount: result.totalCount,
        firstPage: result.animations,
      })
    );
  };

  const loadPage = async (page: number) => {
    if (!currentOrganization?.id) return;

    const result = await AnimationService.GetAnimations(
      currentOrganization.id,
      page + 1,
      pageSize,
      { name: filter }
    );
    if (!result) return;

    dispatch(
      updateAnimationLibrary({
        pageNumber: page,
        page: result.animations,
      })
    );
  };

  const handleRangeChanged = (range: { startIndex: number; endIndex: number }) => {
    const startPage = Math.floor(range.startIndex / pageSize);
    const endPage = Math.floor(range.endIndex / pageSize);

    for (let i = startPage; i <= endPage; i++) {
      if (pagedAnimations[i * pageSize]?.name === unloadedAnimation.name) {
        loadPage(i);
      }
    }
  };

  const handleRefresh = async () => {
    dispatch(resetAnimationLibrary());
    if (!currentOrganization?.id) return;

    setIsRefreshing(true);
    try {
      const result = await AnimationService.GetAnimations(
        currentOrganization.id,
        1,
        pageSize,
        { name: "" }
      );
      if (!result) return;

      dispatch(
        initializeAnimationLibrary({
          totalCount: result.totalCount,
          firstPage: result.animations,
        })
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    pageSize,
    setPageSize,
    filter,
    setFilter,
    handleRangeChanged,
    handleRefresh,
    isRefreshing,
  };
}
